import React from 'react';
import { SHADCN } from '../theme';

// Mini-histogramme du résultat net mensuel, pour le hero « Ce mois ».
//
// Remplace une sparkline normalisée min/max qui ne disait rien : sans ligne de
// zéro, un mois à −200 € et un mois à −5 000 € dessinaient la même courbe, et la
// couleur unique (celle du verdict du mois en cours) peignait en vert des mois
// déficitaires. Ici la ligne de zéro est le repère : au-dessus on gagne, en
// dessous on perd, et la hauteur est proportionnelle au plus gros mois affiché.
//
// Le mois en cours est une projection : barre en creux, bord pointillé.

const MOIS_COURT = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'aoû', 'sep', 'oct', 'nov', 'déc'];
const MOIS_LONG  = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const euros = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(v);

/**
 * @param {Array<{periode: string, net: number, projete?: boolean}>} series
 *   `periode` au format YYYY-MM, `net` en euros (signé), `projete` pour le mois en cours.
 */
export default function NetMiniChart({ series = [], width = 168, height = 56 }) {
  const pts = series.filter(d => Number.isFinite(d.net));
  if (pts.length < 2) return null;

  const LABEL_H = 12;                 // bandeau des mois sous l'axe
  const plot    = height - LABEL_H;
  const pitch   = width / pts.length;
  const barW    = Math.min(16, Math.max(6, pitch - 8));

  // Échelle symétrique autour de zéro : la ligne de zéro se place là où elle
  // tombe réellement, elle n'est pas recentrée au milieu du cadre.
  const maxPos = Math.max(0, ...pts.map(d => d.net));
  const maxNeg = Math.min(0, ...pts.map(d => d.net));
  const span   = (maxPos - maxNeg) || 1;
  const zeroY  = Math.round((maxPos / span) * plot);
  const hauteur = v => Math.max(1, Math.round((Math.abs(v) / span) * plot));

  return (
    <svg width={width} height={height} style={{ flexShrink: 0, overflow: 'visible' }} role="img"
         aria-label={`Résultat net des ${pts.length} derniers mois`}>
      {pts.map((d, i) => {
        const positif = d.net >= 0;
        const couleur = positif ? SHADCN.success : SHADCN.error;
        const h = hauteur(d.net);
        const x = Math.round(i * pitch + (pitch - barW) / 2);
        const y = positif ? zeroY - h : zeroY;
        const mois = Number(d.periode.slice(5, 7)) - 1;
        return (
          <g key={d.periode}>
            <title>{`${MOIS_LONG[mois]}${d.projete ? ' (projection)' : ''} · ${d.net >= 0 ? '+' : '−'}${euros(Math.abs(d.net))}`}</title>
            <rect
              x={x} y={y} width={barW} height={h} rx={2}
              fill={couleur}
              fillOpacity={d.projete ? 0.18 : 0.85}
              stroke={d.projete ? couleur : 'none'}
              strokeWidth={d.projete ? 1 : 0}
              strokeDasharray={d.projete ? '2 2' : undefined}
            />
            <text
              x={x + barW / 2} y={height - 2} textAnchor="middle"
              fontFamily={SHADCN.font} fontSize={9} fill={SHADCN.mutedFg}
            >
              {MOIS_COURT[mois]}
            </text>
          </g>
        );
      })}
      {/* Ligne de zéro — le seul repère qui compte pour un résultat net */}
      <line x1={0} y1={zeroY} x2={width} y2={zeroY} stroke={SHADCN.border} strokeWidth={1} />
    </svg>
  );
}
